import {
  Button,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Grid,
  LinearProgress,
  makeStyles,
  Tooltip,
  Typography,
} from "@material-ui/core";
import React, { FC, useEffect } from "react";
import { useIntl } from "react-intl";
import { connect } from "react-redux";
import { Redirect, RouteComponentProps } from "react-router";
import { bindActionCreators, Dispatch } from "redux";
import { ApplicationState, store } from "../..";
import { ActionCreators as LangActionCreators } from "../../store/session/SessionLanguage";
import { SessionState } from "../../store/session/SessionState";
import { ActionCreators } from "../../store/session/SessionStore";
import { GetTranslation } from "../../translations/GetTranslation";
import TranslatedMessage from "../translations/TranslatedMessage";
import loginImage from "./external-login.jpg";
import loginButtonIcon from "./login-icon.svg";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { fetchUiAppRegistration } from "../../api/SettingsApi";
import { Configuration, InteractionStatus, PublicClientApplication } from "@azure/msal-browser";
import useNotification from "../../utils/useNotification";
import UrlHelper from "../../utils/UrlHelpler";

type LoginProps = RouteComponentProps<{ userType: string }> &
  typeof ActionCreators &
  typeof LangActionCreators &
  SessionState & {
    from: string;
  };

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100%",
  },
  content: {
    padding: "1rem",
    flex: 1,
  },
  footer: {
    backgroundColor: "#0a0a0a",
    padding: "0.5rem",
  },
  footerButton: {
    color: "#fff",
  },
  footerLinksContainer: {
    flex: 1,
    textAlign: "center",
  },
  card: {
    maxWidth: 900,
    margin: "auto",
  },
  media: {
    height: 100,
    "@media (min-width:800px)": { height: 300 },
  },
  card_content: {
    marginLeft: "3rem",
    marginRight: "3rem",
  },
  card_title: {
    marginTop: "1.5rem",
  },
  card_button: {
    paddingTop: "1.5rem",
    paddingBottom: "1.5rem",
    "& img": {
      padding: "1rem",
    },
    "& span": {
      paddingRight: "1rem",
    },
  },
}));

const Login: FC<LoginProps> = ({ detectLanguageIfPossible, location, setAuthenticationClient, setAppRegistration }) => {
  const classes = useStyles();
  const intl = useIntl();
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const defaultDestination = "/";
  const { from } = (location.state as any) || { from: { pathname: defaultDestination } };
  var destination =
    from.pathname === "/login" || from.pathname === "/logout"
      ? defaultDestination
      : typeof from === "string"
      ? from
      : from.pathname;
  const impressumLink = GetTranslation(intl, "login.button.impressumLink");
  const { error } = useNotification();

  var savedDestination = sessionStorage.getItem("destination");
  if (
    !isAuthenticated &&
    !savedDestination &&
    destination !== "/" &&
    destination !== "/login" &&
    destination !== "/logout"
  ) {
    sessionStorage.setItem("destination", destination);
  } else if (isAuthenticated && savedDestination) {
    destination = savedDestination;
  }

  useEffect(() => {
    if (
      !isAuthenticated &&
      inProgress === "none" &&
      destination &&
      destination !== "/" &&
      destination !== "/login" &&
      destination !== "/logout"
    ) {
      instance.loginRedirect({
        scopes: store.getState().sessionState.appRegistration?.scopes ?? [],
        redirectUri: UrlHelper.loginUrl(),
        prompt: "none",
      }).catch((error) => {
        console.error(`Error loginRedirect.`, error);
        var savedDestination = sessionStorage.getItem("destination");
        sessionStorage.clear();
        if (savedDestination) {
          sessionStorage.setItem("destination", savedDestination);
        }
        instance.loginRedirect({
          scopes: store.getState().sessionState.appRegistration?.scopes ?? [],
          redirectUri: UrlHelper.loginUrl(),
          prompt: "select_account",
        });
      });
    }
  }, [destination, inProgress, instance, isAuthenticated]);

  useEffect(() => {
    detectLanguageIfPossible();
  }, [detectLanguageIfPossible]);

  const handleLogin = async (type: any) => {
    localStorage.setItem("lastUserType", type);
    await fetchUiAppRegistration(type)
      .then(async (config) => {
        if (config) {
          setAppRegistration(config);
          const configuration: Configuration = {
            auth: {
              authority: config.authority,
              clientId: config.clientId,
              redirectUri: UrlHelper.loginUrl(),
              knownAuthorities: [config.authority?.split("/")[2]],
            },
            cache: {
              cacheLocation: "localStorage", // Use localStorage to persist the session across tabs
            },
          };

          const clientApp = new PublicClientApplication(configuration);
          setAuthenticationClient(clientApp);
          await clientApp.initialize().then(async () => {
            await clientApp.loginRedirect({ scopes: store.getState().sessionState.appRegistration?.scopes ?? [] });
          });
        } else {
          error("login.error.errorLoadingAppRegistration");
        }
      })
      .catch((err) => {
        error("login.error.errorLoadingAppRegistration", err);
      });
  };

  if (isAuthenticated) {
    sessionStorage.removeItem("destination");
    return <Redirect to={destination} />;
  }

  if (inProgress !== InteractionStatus.None) {
    return <LinearProgress variant="query" />;
  }

  return (
    <Grid container={true} justify="space-between" direction="column" alignItems="center" className={classes.root}>
      <Grid container={true} alignItems="center" className={classes.content}>
        <Card className={classes.card}>
          <CardActionArea onClick={async () => handleLogin("EXTERNAL")}>
            <CardMedia className={classes.media} image={loginImage} title="Anmeldung als Kandidat/Experte" />
            <CardContent>
              <Grid container={true} spacing={3}>
                <Grid item={true} className={classes.card_content}>
                  <Typography gutterBottom variant="h5" component="h2" className={classes.card_title}>
                    <TranslatedMessage id="login.title.external" />
                  </Typography>
                </Grid>
                <Grid item={true} className={classes.card_content}>
                  <Typography component="p">{<TranslatedMessage id="login.text.external1" />}</Typography>
                  <Typography component="p" style={{ marginTop: "0.5rem" }}>
                    <strong style={{ textDecoration: "underline" }}>
                      {<TranslatedMessage id="login.text.externalWarn" />}
                    </strong>
                    {": "}
                    <TranslatedMessage id="login.text.external2" />
                  </Typography>
                </Grid>
                <Grid item={true} container={true} justify="center">
                  <Typography variant="h4">
                    <Tooltip title={<TranslatedMessage id="login.title.external" />}>
                      <Button
                        className={classes.card_button}
                        variant="contained"
                        size="small"
                        style={{ marginLeft: "7px", height: 35, boxShadow: "none" }}
                        color="primary"
                        component="span"
                      >
                        <img src={loginButtonIcon} alt="Login icon" />
                        <span>
                          <TranslatedMessage id="login.button.login" />
                        </span>
                      </Button>
                    </Tooltip>
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </CardActionArea>
        </Card>
      </Grid>
      <Grid container={true} className={classes.footer}>
        <Grid item={true} className={classes.footerLinksContainer}>
          <Button href={impressumLink} color="secondary" className={classes.footerButton}>
            <TranslatedMessage id="login.button.impressum" />
          </Button>
        </Grid>
        <Grid item={true}>
          <Button onClick={async () => handleLogin("INTERNAL")} color="secondary" className={classes.footerButton}>
            <TranslatedMessage id="login.button.adminLogin" />
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators({ ...ActionCreators, ...LangActionCreators }, dispatch);

export default connect((state: ApplicationState) => state.sessionState, mapDispatchToProps)(Login);
